import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { saveDraftFail } from '../../actions/draft';
import { publishOfferFail } from '../../actions/publishInfo';
import { checkServiceAfterFillBalance } from '../../actions/checkoutInfo';
import { updateDepositAccountPayment } from '../../actions/depositAccount';
import {
  processChangingCheckPaymentStatus,
  EPaymentStatusCheckType,
} from '../../thunks/payment/processChangingCheckPaymentStatus';
import { checkPaymentStatus } from '../../thunks/payment/checkPaymentStatus';
import { loadPublishTerms } from '../../thunks/publishTerms/loadPublishTerms';
import { EPaymentProvider } from '../../../PaymentWidgetMicrofrontend';
import { useDispatch } from 'react-redux';
import { EPaymentType } from '../../reducers/v2';

interface IWebviewHandlersWrapper {
  children: React.ReactElement;
}

interface ISuccessPaymentHandlerParams {
  paymentId: string;
  provider: EPaymentProvider;
  paymentType?: EPaymentStatusCheckType;
}

export const WebviewHandlersWrapper = ({ children }: IWebviewHandlersWrapper) => {
  const dispatch = useDispatch();

  const refillBalanceSuccessHandler = useCallback(
    (params: ISuccessPaymentHandlerParams | undefined) => {
      if (params && typeof params === 'object') {
        dispatch(
          updateDepositAccountPayment({
            paymentId: params.paymentId,
            provider: params.provider || EPaymentProvider.YooKassa,
            type: params.paymentType === EPaymentStatusCheckType.Package ? EPaymentType.Package : EPaymentType.Balance,
          }),
        );

        dispatch(
          checkPaymentStatus({
            onChangePaymentStatus: processChangingCheckPaymentStatus(
              params.paymentType || EPaymentStatusCheckType.Balance,
            ),
            paymentId: params.paymentId,
            provider: params.provider || EPaymentProvider.YooKassa,
          }),
        );
      } else {
        dispatch(checkServiceAfterFillBalance());
      }
    },
    [dispatch],
  );

  const publishOfferFailHandler = useCallback(() => {
    dispatch(publishOfferFail());
  }, [dispatch]);

  const saveOfferDraftFailHandler = useCallback(() => {
    dispatch(saveDraftFail());
  }, [dispatch]);

  const buyPackageSuccessHandler = useCallback(
    (params: ISuccessPaymentHandlerParams | undefined) => {
      // Если есть paymentId – покупка с пополнением/холдированием, проверяем статус платежа
      if (params && typeof params === 'object' && 'paymentId' in params) {
        refillBalanceSuccessHandler({ ...params, paymentType: EPaymentStatusCheckType.Package });
      } else {
        // нет paymentId – покупка со счета пользователя, обновляем publishTerms
        dispatch(loadPublishTerms());
      }
    },
    [dispatch, refillBalanceSuccessHandler],
  );

  useEffect(() => {
    window.appInterfaceV2.refillBalanceSuccess = refillBalanceSuccessHandler;
    window.appInterfaceV2.refillBalanceCancel = () => {};
    window.appInterfaceV2.publishOfferFail = publishOfferFailHandler;
    window.appInterfaceV2.saveOfferDraftFail = saveOfferDraftFailHandler;
    window.appInterfaceV2.buyPackageSuccess = buyPackageSuccessHandler;

    return () => {
      delete window.appInterfaceV2.refillBalanceSuccess;
      delete window.appInterfaceV2.refillBalanceCancel;
      delete window.appInterfaceV2.publishOfferFail;
      delete window.appInterfaceV2.saveOfferDraftFail;
      delete window.appInterfaceV2.buyPackageSuccess;
    };
  }, [buyPackageSuccessHandler, publishOfferFailHandler, refillBalanceSuccessHandler, saveOfferDraftFailHandler]);

  return children;
};
